import { FilterPills, type PillItem } from '@/components/FilterPills';
import { ImageThumbnail, type ThumbnailImage } from '@/components/ImageThumbnail';
import { PageHeader, PageShell } from '@/components/PageShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { allEquipmentTypesState, equipmentTestLibraryState, spacesState } from '@/state';
import { useDebouncedSearchParam, useSetSearchParams } from '@/lib/searchParams';
import { spacePillLabel } from '@/lib/spaceLabels';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import {
    type EquipmentTestLibraryEntry,
    type EquipmentType,
    type EquipmentTypeId,
    isOfferableType,
    type SpaceId,
} from '@rock/types';
import { Edit, ImageOff } from 'lucide-react';
import { orderBy } from 'lodash';
import { type FC, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EntryDetailDialog } from './EntryDetailDialog';
import { EntryFormDialog, type EntryPrefill } from './EntryFormDialog';
import { isPhotographableType } from './libraryTypes';

/** Card tile edge, in px — two of these sit side by side inside the narrowest card. */
const CARD_THUMBNAIL_PX = 100;

/**
 * The equipment test library: product + nameplate photos for equipment the team can't put hands on,
 * displayed large so a phone running the app can scan them off the screen.
 *
 * Filtering is a drill-down — space, then equipment type, then brand — and every level is optional:
 * picking HVAC and stopping is a complete filter. The result list is always on screen and just
 * narrows as you click, so you can see what you're doing to it.
 *
 * All of it runs in memory over one subscription. The library is a curated set in the
 * dozens-to-low-hundreds, so a filter click should be a re-render, not a round trip — and the pill
 * counts need the unfiltered set anyway.
 */
export const TestLibraryListPage: FC = () => {
    const entries = useAsyncAtomValue(equipmentTestLibraryState, []);
    const spaces = useAsyncAtomValue(spacesState, []);
    const equipmentTypes = useAsyncAtomValue(allEquipmentTypesState, []);

    // Filters live in the URL, matching the models/brands lists: they survive a refresh and the
    // card → detail → back round trip, and a filtered view is a link you can hand to someone.
    const [searchParams] = useSearchParams();
    const setFilters = useSetSearchParams();
    const spaceFilter = (searchParams.get('space') as SpaceId | null) ?? undefined;
    const typeFilter = (searchParams.get('type') as EquipmentTypeId | null) ?? undefined;
    const brandFilter = searchParams.get('brand') ?? undefined;
    const showEmpty = searchParams.get('empty') === '1';
    const hideOutdated = searchParams.get('outdated') === 'hide';
    // The input stays responsive while the URL (the source of truth for filtering) debounces.
    const { input: searchInput, onChange: onSearchChange } = useDebouncedSearchParam('q');
    const search = searchParams.get('q') ?? '';

    const [detailEntry, setDetailEntry] = useState<EquipmentTestLibraryEntry | undefined>();
    const [formEntry, setFormEntry] = useState<EquipmentTestLibraryEntry | undefined>();
    const [formOpen, setFormOpen] = useState(false);
    const [formPrefill, setFormPrefill] = useState<EntryPrefill | undefined>();

    const typesById = useMemo(() => new Map(equipmentTypes.map((type) => [type.id, type])), [equipmentTypes]);

    // Text search and Hide Outdated sit ABOVE the drill-down: they narrow the pool every pill row counts against.
    const pool = useMemo(() => {
        const term = search.trim().toLowerCase();
        return entries.filter((entry) => {
            if (hideOutdated && entry.outdated) return false;
            if (!term) return true;
            return [entry.name, entry.brand, entry.modelNumber, entry.serialNumber, typesById.get(entry.typeId)?.title]
                .filter(Boolean)
                .some((field) => (field as string).toLowerCase().includes(term));
        });
    }, [entries, search, typesById, hideOutdated]);

    const inSpace = useMemo(
        () => (spaceFilter ? pool.filter((entry) => entry.spaceId === spaceFilter) : pool),
        [pool, spaceFilter],
    );
    const inType = useMemo(
        () => (typeFilter ? inSpace.filter((entry) => entry.typeId === typeFilter) : inSpace),
        [inSpace, typeFilter],
    );
    const visible = useMemo(
        () => (brandFilter ? inType.filter((entry) => (entry.brand ?? 'Unbranded') === brandFilter) : inType),
        [inType, brandFilter],
    );

    // `spaces` arrives in the app's dashboard order (see spacesState), so map/filter preserves it.
    const spacePills: PillItem[] = useMemo(
        () =>
            spaces
                .map((space) => ({
                    key: space.id,
                    label: spacePillLabel(space),
                    count: pool.filter((entry) => entry.spaceId === space.id).length,
                }))
                .filter((pill) => pill.count > 0 || showEmpty),
        [spaces, pool, showEmpty],
    );

    // Types are only offered once a space is chosen — the full 119-type catalog as pills would be
    // noise. "Show Empty Filters" widens this from "types I have material for" to "every type in this
    // space", so a (0) pill is the prompt to go source one.
    const typePills: PillItem[] = useMemo(() => {
        if (!spaceFilter) return [];
        const counts = new Map<EquipmentTypeId, number>();
        for (const entry of inSpace) {
            counts.set(entry.typeId, (counts.get(entry.typeId) ?? 0) + 1);
        }
        const candidates = showEmpty
            ? orderBy(
                  equipmentTypes.filter(
                      (type) =>
                          type.spaceId === spaceFilter &&
                          isPhotographableType(type) &&
                          (isOfferableType(type) || counts.has(type.id)),
                  ),
                  ['order', 'title'],
              )
            : orderBy(
                  [...counts.keys()]
                      .map((id) => typesById.get(id))
                      .filter((type): type is EquipmentType => !!type && isPhotographableType(type)),
                  ['order', 'title'],
              );
        return candidates.map((type) => ({ key: type.id, label: type.title, count: counts.get(type.id) ?? 0 }));
    }, [spaceFilter, inSpace, equipmentTypes, typesById, showEmpty]);

    const brandPills: PillItem[] = useMemo(() => {
        if (!spaceFilter) return [];
        const counts = new Map<string, number>();
        for (const entry of inType) {
            const brand = entry.brand ?? 'Unbranded';
            counts.set(brand, (counts.get(brand) ?? 0) + 1);
        }
        return orderBy(
            [...counts.entries()].map(([brand, count]) => ({ key: brand, label: brand, count })),
            ['label'],
        );
    }, [spaceFilter, inType]);

    // Each level clears the ones below it in a single write — no intermediate state where a type
    // filter outlives the space it belonged to.
    const selectSpace = (key: string | undefined) => setFilters({ space: key, type: undefined, brand: undefined });
    const selectType = (key: string | undefined) => setFilters({ type: key, brand: undefined });
    const selectBrand = (key: string | undefined) => setFilters({ brand: key });

    const openAdd = (prefill?: EntryPrefill) => {
        setFormEntry(undefined);
        setFormPrefill(prefill);
        setFormOpen(true);
    };

    const openEdit = (entry: EquipmentTestLibraryEntry) => {
        setFormEntry(entry);
        setFormPrefill(undefined);
        setFormOpen(true);
    };

    return (
        <PageShell>
            <PageHeader
                title="Test Library"
                description="Equipment photos for testing the app's scan and guide paths — point a phone at the screen."
            >
                <div className="flex items-center gap-2">
                    <Switch
                        id="show-empty"
                        checked={showEmpty}
                        onCheckedChange={(on) => setFilters({ empty: on ? '1' : undefined })}
                    />
                    <Label htmlFor="show-empty" className="whitespace-nowrap text-sm font-normal">
                        Show Empty Filters
                    </Label>
                </div>
                <div className="flex items-center gap-2">
                    <Switch
                        id="hide-outdated"
                        checked={hideOutdated}
                        onCheckedChange={(on) => setFilters({ outdated: on ? 'hide' : undefined })}
                    />
                    <Label htmlFor="hide-outdated" className="whitespace-nowrap text-sm font-normal">
                        Hide Outdated
                    </Label>
                </div>
                <Input
                    value={searchInput}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder="Name, brand, model, serial"
                    className="w-64"
                    aria-label="Search the library"
                />
                <Button type="button" onClick={() => openAdd()}>
                    Add entry
                </Button>
            </PageHeader>

            <div className="space-y-3 rounded border bg-background p-3">
                <FilterPills
                    label="Space"
                    items={spacePills}
                    selected={spaceFilter}
                    allCount={pool.length}
                    onSelect={selectSpace}
                />
                {spaceFilter && (
                    <FilterPills
                        label="Type"
                        items={typePills}
                        selected={typeFilter}
                        allCount={inSpace.length}
                        onSelect={selectType}
                    />
                )}
                {spaceFilter && (
                    <FilterPills
                        label="Brand"
                        items={brandPills}
                        selected={brandFilter}
                        allCount={inType.length}
                        onSelect={selectBrand}
                    />
                )}
            </div>

            <div className="text-sm text-muted-foreground">
                {visible.length} {visible.length === 1 ? 'entry' : 'entries'}
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
                {visible.map((entry) => (
                    <div key={entry.id} className="group/card relative">
                        <button
                            type="button"
                            className="w-full cursor-pointer overflow-hidden rounded border text-left hover:border-primary"
                            onClick={() => setDetailEntry(entry)}
                        >
                            {/* Both photos, so the card answers "does this have a usable plate?" without
                                opening it — the plate is the whole reason an entry exists. Cover-cropped
                                at rest so a row of cards reads evenly, easing to the whole image on
                                hover (the same tile the equipment gallery uses). The detail modal is
                                deliberately the opposite: there a photo is always whole, because that's
                                the one being scanned. */}
                            <div className="flex h-28 items-center justify-center gap-1 bg-slate-50 p-1">
                                {entry.productImage || entry.nameplateImage ? (
                                    <>
                                        <CardPhoto image={entry.productImage} alt={`${entry.name} product`} />
                                        <CardPhoto image={entry.nameplateImage} alt={`${entry.name} nameplate`} />
                                    </>
                                ) : (
                                    <ImageOff className="h-6 w-6 text-muted-foreground" />
                                )}
                            </div>
                            <div className="space-y-1 p-2">
                                <div className="truncate text-sm font-medium">{entry.name}</div>
                                <div className="truncate text-xs text-muted-foreground">
                                    {typesById.get(entry.typeId)?.title ?? entry.typeId}
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {entry.modelNumber && (
                                        <Badge variant="outline" className="text-[10px]">
                                            {entry.modelNumber}
                                        </Badge>
                                    )}
                                    {entry.outdated && (
                                        <Badge variant="secondary" className="text-[10px]">
                                            Outdated
                                        </Badge>
                                    )}
                                    {!entry.nameplateImage && (
                                        <Badge variant="secondary" className="text-[10px]">
                                            No plate
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </button>
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="absolute right-2 top-2 opacity-0 transition-opacity max-md:opacity-100 group-hover/card:opacity-100"
                            onClick={() => openEdit(entry)}
                        >
                            <Edit /> Edit
                        </Button>
                    </div>
                ))}
            </div>

            {visible.length === 0 && (
                <div className="space-y-3 rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
                    <div>Nothing matches these filters yet.</div>
                    {/* Selecting a (0) type is how you ask for material that doesn't exist — so offer to add it. */}
                    {typeFilter && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => openAdd({ typeId: typeFilter, spaceId: spaceFilter })}
                        >
                            Add an entry for {typesById.get(typeFilter)?.title ?? typeFilter}
                        </Button>
                    )}
                </div>
            )}

            <EntryDetailDialog
                entry={detailEntry}
                onOpenChange={(open) => {
                    if (!open) setDetailEntry(undefined);
                }}
                onEdit={() => {
                    const editing = detailEntry;
                    setDetailEntry(undefined);
                    if (editing) openEdit(editing);
                }}
            />

            <EntryFormDialog open={formOpen} onOpenChange={setFormOpen} entry={formEntry} prefill={formPrefill} />
        </PageShell>
    );
};

/**
 * One half of a card's photo row. An absent photo still occupies its half rather than letting the
 * other one recentre — a missing plate should read as a gap, since that's the entry's whole problem.
 */
const CardPhoto: FC<{ image: ThumbnailImage | undefined; alt: string }> = ({ image, alt }) =>
    image ? (
        <ImageThumbnail image={image} alt={alt} size={CARD_THUMBNAIL_PX} />
    ) : (
        <div
            className="flex flex-shrink-0 items-center justify-center rounded-md bg-muted"
            style={{ width: CARD_THUMBNAIL_PX, height: CARD_THUMBNAIL_PX }}
        >
            <ImageOff className="h-5 w-5 text-muted-foreground" />
        </div>
    );
